// ============================================
// IRenderContext - Backend-agnostic Interface
// ============================================

import {
  type GPUBackend,
  type TextureHandle,
  type BufferHandle,
  type PipelineHandle,
  type ShaderHandle,
  type RenderTargetHandle,
  type MRTRenderTargetHandle,
  type ComputePipelineHandle,
  type TextureDescriptor,
  type BufferDescriptor,
  type PipelineDescriptor,
  type ComputePipelineDescriptor,
  type ComputeBindGroup,
  type DispatchSize,
  type RenderStats,
  type BlendMode,
  type VertexBufferLayout,
  type MRTRenderTargetDescriptor,
  type MRTRenderTargetInfo,
} from '../types';

/**
 * Abstract render context interface
 * Implemented by WebGPURenderContext
 */
export interface IRenderContext {
  readonly backend: GPUBackend;
  readonly canvas: HTMLCanvasElement;
  
  /** Get underlying GPU device (WebGPU only, for resource sharing) */
  getDevice?(): GPUDevice | null;
  
  // ============================================
  // Resource Creation
  // ============================================
  
  createTexture(desc: TextureDescriptor): TextureHandle;
  createTextureFromImage(image: TexImageSource, options?: Partial<TextureDescriptor>): TextureHandle;
  updateTexture(handle: TextureHandle, data: ArrayBufferView | TexImageSource, x?: number, y?: number, width?: number, height?: number): void;
  
  createBuffer(desc: BufferDescriptor): BufferHandle;
  updateBuffer(handle: BufferHandle, data: ArrayBufferView, offset?: number): void;
  
  createShader(vertexSource: string, fragmentSource: string): ShaderHandle;
  createPipeline(desc: PipelineDescriptor): PipelineHandle;
  createRenderTarget(width: number, height: number, hasDepth?: boolean, hdr?: boolean): RenderTargetHandle;
  
  // ============================================
  // MRT (Multiple Render Targets)
  // ============================================
  
  /** Create a render target with multiple color attachments (for GBuffer, etc.). */
  createMRTRenderTarget(desc: MRTRenderTargetDescriptor): MRTRenderTargetHandle;
  
  /** Destroy an MRT render target and all its attachments. */
  destroyMRTRenderTarget(handle: MRTRenderTargetHandle): void;
  
  /** Bind an MRT render target for rendering. All color attachments are bound simultaneously. */
  setMRTRenderTarget(handle: MRTRenderTargetHandle): void;
  
  /** Get info about an MRT render target (texture handles, dimensions, etc.). */
  getMRTRenderTargetInfo(handle: MRTRenderTargetHandle): MRTRenderTargetInfo | null;

  // ============================================
  // Resource Destruction
  // ============================================
  
  destroyTexture(handle: TextureHandle): void;
  destroyBuffer(handle: BufferHandle): void;
  destroyPipeline(handle: PipelineHandle): void;
  destroyShader(handle: ShaderHandle): void;
  destroyRenderTarget(handle: RenderTargetHandle): void;
  
  // ============================================
  // Frame Control
  // ============================================
  
  beginFrame(): void;
  endFrame(): void;
  getStats(): RenderStats;

  /**
   * Optional GPU-timing markers: label the GPU passes recorded until the matching
   * pop, so timestamp-query results are attributed to a render-pipeline section.
   * No-op on backends without timestamp support.
   */
  pushGpuMarker?(label: string): void;
  popGpuMarker?(): void;

  // ============================================
  // Render Target
  // ============================================
  
  setRenderTarget(handle: RenderTargetHandle | null): void;
  getRenderTargetTexture(handle: RenderTargetHandle): TextureHandle;

  /**
   * Copy the CURRENT backbuffer (swapchain) contents into a sampleable texture (formats must
   * match — use an LDR render-target's color texture). Ends the open render pass; the caller
   * re-binds its target afterwards (passes reopen with loadOp 'load', so content survives).
   * Optional capability: false = unsupported/failed and the caller should degrade gracefully
   * (UI backdrop blur skips the blur draw). Only valid while rendering to the backbuffer.
   */
  copyBackbufferToTexture?(dst: TextureHandle, width: number, height: number): boolean;
  
  // ============================================
  // State Setting
  // ============================================
  
  setPipeline(handle: PipelineHandle): void;
  bindTexture(slot: number, handle: TextureHandle): void;
  bindUniforms?(): void; // Bind uniform buffer only (for texture-less pipelines like VFX)
  bindVertexBuffer(handle: BufferHandle): void;
  bindIndexBuffer(handle: BufferHandle): void;
  setUniform(name: string, value: number | Float32Array | Int32Array): void;
  setUniformInt(name: string, value: number): void;
  setupVertexAttributes(layout: VertexBufferLayout): void;

  /**
   * Snapshot the current uniform data into a dedicated GPU buffer so that
   * subsequent draw calls in the same render pass see isolated uniform state.
   *
   * Without this, multiple draw calls that change uniforms via `setUniform`
   * will all read the **last** written values because `queue.writeBuffer`
   * completes before the render pass executes on the GPU.
   *
   * Call this after `setUniform` and before `bindTexture` / `draw*`.
   */
  captureUniforms?(): void;

  /**
   * Bind lighting data for forward-lit pipelines (group 1).
   * Creates a bind group from the given uniform + storage buffer handles.
   * Must be called before drawing with a `hasLighting` pipeline.
   */
  setLightingData?(uniformBuffer: BufferHandle, storageBuffer: BufferHandle): void;

  /** Clear the lighting bind group (forward-lit no longer needed). */
  clearLightingData?(): void;

  /**
   * Bind Forward+ lighting data (group 1) with tile light index buffer, shadow map and IES profiles.
   * Creates a bind group from uniform + light storage + tile data storage + shadow map + IES profiles.
   * Must be called before drawing with a `hasForwardPlus` pipeline.
   * @param shadowMapBuffer — optional shadow atlas buffer; if omitted, a tiny dummy is used.
   * @param iesProfileBuffer — optional IES profile data buffer; if omitted, a tiny dummy is used.
   */
  setForwardPlusData?(uniformBuffer: BufferHandle, storageBuffer: BufferHandle, tileLightBuffer: BufferHandle, shadowMapBuffer?: BufferHandle, iesProfileBuffer?: BufferHandle): void;

  /** Clear the Forward+ bind group. */
  clearForwardPlusData?(): void;
  
  // ============================================
  // Viewport / Scissor
  // ============================================
  
  setViewport(x: number, y: number, width: number, height: number): void;
  setScissor(x: number, y: number, width: number, height: number): void;
  disableScissor(): void;
  
  // ============================================
  // Blend Mode
  // ============================================

  /**
   * @deprecated No-op in the WebGPU backend — blend state belongs to the pipeline. Blend is baked
   * into pipelines by Material (renderState.blendMode) and SpriteBatch (blend pipeline variants).
   * Retained so existing implementors (native player, test mocks) keep compiling.
   */
  setBlendMode(mode: BlendMode): void;
  
  // ============================================
  // Drawing
  // ============================================
  
  clear(r: number, g: number, b: number, a?: number): void;
  draw(vertexCount: number, first?: number): void;
  drawIndexed(indexCount: number, first?: number): void;
  /** Draw instanced primitives */
  drawInstanced?(vertexCount: number, instanceCount: number, firstVertex?: number, firstInstance?: number): void;
  
  // ============================================
  // Resource Info
  // ============================================
  
  getTextureSize(handle: TextureHandle): { width: number; height: number } | null;
  
  /** Get the swap chain / presentation format for the canvas */
  getSwapChainFormat(): import('../types').TextureFormat;
  
  /** Handle canvas resize - must be called after changing canvas.width/height */
  handleResize?(): void;
  
  // ============================================
  // Compute (GPU Compute Shaders)
  // ============================================
  
  /** Check if compute shaders are supported */
  readonly supportsCompute: boolean;
  
  /** Create a compute shader from source */
  createComputeShader?(source: string): ShaderHandle;
  
  /** Create a compute pipeline */
  createComputePipeline?(desc: ComputePipelineDescriptor): ComputePipelineHandle;
  
  /** Destroy a compute pipeline */
  destroyComputePipeline?(handle: ComputePipelineHandle): void;
  
  /** Set current compute pipeline */
  setComputePipeline?(handle: ComputePipelineHandle): void;
  
  /** Bind resources for compute */
  bindComputeResources?(bindings: ComputeBindGroup): void;
  
  /** Dispatch compute work */
  dispatchCompute?(size: DispatchSize): void;
  
  /** Dispatch compute work with indirect buffer */
  dispatchComputeIndirect?(buffer: BufferHandle, offset?: number): void;
  
  /** Insert a memory barrier (ensure compute writes are visible) */
  computeBarrier?(): void;
  
  /** Flush compute work - submit command buffer to make results available */
  flushCompute?(): void;
  
  // ============================================
  // Debug Markers (for PIX, RenderDoc, etc.)
  // ============================================
  
  /** Push a debug group (nested markers) */
  pushDebugGroup?(label: string): void;
  
  /** Pop the current debug group */
  popDebugGroup?(): void;
  
  /** Insert a debug marker (single point) */
  insertDebugMarker?(label: string): void;
}

/**
 * Render context creation options
 */
export interface RenderContextOptions {
  canvas: HTMLCanvasElement;
  backend?: GPUBackend;
  alpha?: boolean;
  antialias?: boolean;
  powerPreference?: 'default' | 'low-power' | 'high-performance';
  premultipliedAlpha?: boolean;
  preserveDrawingBuffer?: boolean;
  /** Share existing WebGPU device (for multi-canvas scenarios) */
  sharedDevice?: GPUDevice;
  /** Share resource maps from existing context (makes handles work across contexts) */
  sharedContext?: IRenderContext;
}

/**
 * Check if WebGPU is available
 */
export function isWebGPUAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'gpu' in navigator;
}
